<template>
  <div id="prop_trading_payout">
    <div class="card">
      <div class="content-box">
        <div class="title">{{ $t('propTrade.payout') }}</div>
        <el-form class="form-box" :model="form" :rules="formRules" ref="form">
          <div class="refund" v-if="hasRefund" :class="[{ 'has-cc': hasCreditCard }]">
            <div class="label_title">
              <div class="sub_label">
                <div>{{ $t('propTrade.payoutPage.refund') }} -&nbsp;</div>
                <span v-if="hasCreditCard">{{ $t('common.withdrawChannel.creditcard') }}</span>
                <span v-else
                  >{{ firstLetterUpperCase(form.paymentMethod) }}
                  {{
                    form.paymentMethod === 'crypto'
                      ? langTranslate('deposit.default.channels.', form.crypto)
                      : langTranslate('common.withdrawChannel.', form.ewallet)
                  }}</span
                >
              </div>
              <div class="label_amount">
                {{ account.currency | currencyToSymbol }} {{ info.refundAmount | fixedDecimal(2) }}
              </div>
            </div>
            <div class="refund_detail" v-if="hasCreditCard">
              <el-row :gutter="30">
                <el-col :xs="24" :md="12">
                  <el-form-item :label="$t('propTrade.payoutPage.ccNum')">
                    <div class="disabled_input">{{ creditCard.cardNumber }}</div>
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :md="12">
                  <el-form-item :label="$t('propTrade.payoutPage.amt')">
                    <div class="disabled_input">{{ info.refundAmount | fixedDecimal(2) }}</div>
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :md="12">
                  <el-form-item :label="$t('propTrade.payoutPage.cardHolderName')">
                    <div class="disabled_input">{{ creditCard.cardHolderName }}</div>
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :md="12">
                  <el-form-item :label="$t('propTrade.payoutPage.expDate')">
                    <div class="disabled_input">
                      <img class="calendar_icon" src="@/assets/images/propTrade/calendar.png" />
                      {{ creditCard.cardExpiryDate }}
                    </div>
                  </el-form-item>
                </el-col>
              </el-row>
            </div>
          </div>
          <div class="profit_split">
            <div class="label_title">
              <div class="sub_label">
                <div>
                  {{ $t('propTrade.payoutPage.profitSplit') }} - {{ $t('propTrade.payoutPage.paymentChannel') }}
                </div>
              </div>
              <div class="label_amount">
                {{ account.currency | currencyToSymbol }} {{ info.profitSplit | fixedDecimal(2) }}
              </div>
            </div>
            <div class="payment-method mt-5">
              <el-form-item prop="paymentMethod">
                <div class="payment-method-radio-box">
                  <div class="payment-method-item" v-for="item in calPayoutMethodList" :key="item.key">
                    <el-radio-group
                      v-model="form.paymentMethod"
                      @input="setPaymentDetailDefault"
                      data-testid="paymentMethod"
                    >
                      <PaymentMethodRadioButton :name="item.name" :value="item.key" />
                    </el-radio-group>
                  </div>
                </div>
              </el-form-item>
            </div>
            <div
              class="payment-method-group"
              v-if="form.paymentMethod === 'crypto' || form.paymentMethod === 'ewallet'"
            >
              <h5 class="sub-title">{{ $t('propTrade.createAccount.subscriptionDetail.select') }}</h5>
              <el-form-item prop="crypto" v-if="form.paymentMethod === 'crypto'">
                <div class="payment-radio">
                  <el-radio-group v-model="form.crypto">
                    <div
                      v-for="(payment, index) in cryptoList"
                      :key="`ptCrypto${index}`"
                      @click="changeCryptoPayoutDetail(payment)"
                    >
                      <el-radio :label="payment.label">{{
                        langTranslate('deposit.default.channels.', payment.label)
                      }}</el-radio>
                    </div>
                  </el-radio-group>
                </div>
              </el-form-item>
              <el-form-item prop="ewallet" v-if="form.paymentMethod === 'ewallet'">
                <div class="payment-radio">
                  <el-radio-group v-model="form.ewallet">
                    <div
                      v-for="(payment, index) in ewalletList"
                      :key="`ptEwallet${index}`"
                      @click="changeCPSPayoutDetail(payment)"
                    >
                      <el-radio :label="payment.label">{{
                        langTranslate('common.withdrawChannel.', payment.label)
                      }}</el-radio>
                    </div>
                  </el-radio-group>
                </div>
              </el-form-item>
            </div>
            <div class="withdraw-container">
              <div class="crypto-withdraw" v-if="form.paymentMethod === 'crypto'">
                <component
                  :is="currentComponent"
                  :accountNumber="Number(routerAccountNumber)"
                  :amount="info.profitSplit"
                  :withdrawalType="cryptoSelectWithdrawType"
                  :withdrawalPayment="withdrawalPayment"
                  ref="cryptoComponent"
                ></component>
              </div>
              <div class="ewallet-withdraw" v-if="form.paymentMethod === 'ewallet'">
                <!-- 需要从store拿，传入特定状态，与AU差异化，在OtherMethod组件复用该组件 -->
                <PtCpsWithdraw
                  :accountNumber="Number(routerAccountNumber)"
                  :amount="info.profitSplit"
                  :withdrawalType="cpsWithdrawType"
                  :currency="account.currency"
                  ref="cpsComponent"
                  :withdrawalPayment="withdrawalPayment"
                />
              </div>
            </div>
          </div>
          <div class="summary">
            <div class="summary_title">{{ $t('propTrade.payoutPage.summary') }}</div>
            <div class="summary_detail">
              <ul>
                <li>
                  <span class="label">{{ $t('propTrade.payoutPage.sessionNumber') }}</span>
                  <span class="join_line"></span>
                  <span class="value">{{ account.sessionNumber }}</span>
                </li>
                <li>
                  <span class="label">{{ $t('propTrade.payoutPage.accID') }}</span>
                  <span class="join_line"></span>
                  <span class="value">{{ account.accountNumber }}</span>
                </li>
                <li>
                  <span class="label">{{ $t('propTrade.payoutPage.currency') }}</span>
                  <span class="join_line"></span>
                  <span class="value">{{ account.currency }}</span>
                </li>
                <li class="dash"></li>
                <li>
                  <span class="label">{{
                    $t('propTrade.payoutPage.profitsplit', { percent: `${info.profitSplitPercent}%` })
                  }}</span>
                  <span class="join_line"></span>
                  <span class="value">{{ info.profitSplit | fixedDecimal(2) }} {{ account.currency }}</span>
                </li>
                <li v-if="info.refundAmount > 0">
                  <span class="lable">{{ $t('propTrade.payoutPage.subscriptionFeeRefund') }}</span>
                  <span class="join_line"></span>
                  <span class="value">{{ info.refundAmount | fixedDecimal(2) }} {{ account.currency }}</span>
                </li>
                <li v-if="info.profitSplitFee > 0">
                  <span class="lable">
                    {{
                      $t('propTrade.payoutPage.profitSplitFee', {
                        channel:
                          form.paymentMethod === 'crypto'
                            ? langTranslate('deposit.default.channels.', form.crypto)
                            : langTranslate('common.withdrawChannel.', form.ewallet)
                      })
                    }}
                  </span>
                  <span class="join_line"></span>
                  <span class="value minus">-{{ info.profitSplitFee | fixedDecimal(2) }} {{ account.currency }}</span>
                </li>
                <li v-if="info.refundAmountFee > 0">
                  <span class="lable">{{ $t('propTrade.payoutPage.refundAmountFee') }}</span>
                  <span class="join_line"></span>
                  <span class="value minus">-{{ info.refundAmountFee | fixedDecimal(2) }} {{ account.currency }}</span>
                </li>
                <li class="total">
                  <span class="label">
                    <span>{{ $t('propTrade.payoutPage.totalEligiblePayoutAmount') }}</span>
                    <InfoPopover popoverWidth="220">
                      <template v-slot:popoverContent>
                        {{ $t('propTrade.payoutPage.totalEligiblePayoutAmountInfo') }}
                      </template>
                    </InfoPopover>
                  </span>
                  <span class="value">{{ info.payoutAmount | fixedDecimal(2) }} {{ account.currency }}</span>
                </li>
              </ul>
            </div>
          </div>
          <div class="btn-box">
            <div class="back-button">
              <el-button type="primary" :class="'action-button'" data-testId="back" @click="goBackToChooseAccount">{{
                $t('propTrade.button.back')
              }}</el-button>
            </div>
            <div class="confirm-button">
              <el-button class="blue_button action-button" type="primary" data-testId="confirm" @click="submit">{{
                $t('propTrade.button.payout')
              }}</el-button>
            </div>
          </div>
        </el-form>
      </div>
      <InfoDialog
        :visible.sync="showSuccessDialog"
        type="success"
        :enableCloseOnBackground="true"
        :subTitle="$t('propTrade.payoutPage.congratulations')"
        @close="closeDialog"
        custom-class="ptDialog"
        :append-to-body="false"
      >
        <div slot="content">
          <div>{{ $t('propTrade.payoutPage.payoutSuccessDialog') }}</div>
          <div class="button-wrapper">
            <el-button class="blue_button" type="primary" @click="showSuccessDialog = false">{{
              $t('common.button.ok')
            }}</el-button>
          </div>
        </div>
      </InfoDialog>
      <InfoDialog
        :visible.sync="showErrorDialog"
        :closeOnClickModal="true"
        :subTitle="$t('propTrade.payoutPage.payoutRequestIsUnsuccessful')"
        type="fail"
        @close="closeDialog"
        custom-class="ptDialog"
        :append-to-body="false"
      >
        <div slot="content">
          <div class="desc_title">{{ $t('propTrade.payoutPage.payoutRequestIsUnsuccessfulDesc') }}</div>
          <div class="desc">{{ $t('propTrade.payoutPage.payoutRequestIsUnsuccessfulDesc2') }}</div>
          <div class="desc">{{ $t('propTrade.payoutPage.payoutRequestIsUnsuccessfulDesc3') }}</div>
          <div class="button-wrapper">
            <el-button class="blue_button" type="primary" @click="showErrorDialog = false">{{
              $t('common.button.ok')
            }}</el-button>
          </div>
        </div>
      </InfoDialog>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import { statusConfig } from '@/constants/status';
import { ACCOUNT_TYPE_REV, PAYOUT_METHOD } from '@/constants/propTrade';
import { apiPayoutInfo, apiGetAccountMetrics, apiPropTradingPayout } from '@/resource/propTrading';
import { apiPaymentGetInfo } from '@/resource';
import TextInput from '../components/common/TextInput.vue';
import InfoPopover from '../components/common/InfoPopover.vue';
import InfoDialog from '../components/common/InfoDialog.vue';
import PaymentMethodRadioButton from '@/components/propTrading/components/createAccount/PaymentMethodRadioButton.vue';
// import PtBitcoinWithdraw from '@/components/propTrading/components/payout/withdraw/PtBitcoinWithdraw.vue';
// import PtEthWithdraw from '@/components/propTrading/components/payout/withdraw/PtEthWithdraw.vue';
// import PtUsdtWithdraw from '@/components/propTrading/components/payout/withdraw/PtUsdtWithdraw.vue';
// import PtUsdcWithdraw from '@/components/propTrading/components/payout/withdraw/PtUsdcWithdraw.vue';
import PtCpsWithdraw from '@/components/propTrading/components/payout/withdraw/PtCpsWithdraw.vue';
import mixin from '@/mixins';
import cpsWithdrawlalMixin from '@/mixins/page/propTrade/cpsWithdrawal';
import propTradeCommonFilters from '@/mixins/page/propTrade/commonFilters';
import { isSuccess } from '@/util/utils';

export default {
  name: 'Payout',
  components: {
    TextInput,
    InfoPopover,
    InfoDialog,
    PaymentMethodRadioButton,
    // PtBitcoinWithdraw,
    // PtEthWithdraw,
    // PtUsdtWithdraw,
    // PtUsdcWithdraw,
    PtCpsWithdraw
  },
  mixins: [mixin, cpsWithdrawlalMixin, propTradeCommonFilters],
  data() {
    return {
      showErrorDialog: false,
      showSuccessDialog: false,
      // withdrawalTypesMap: {
      //   34: {
      //     component: PtBitcoinWithdraw
      //   },
      //   36: {
      //     component: PtUsdtWithdraw
      //   },
      //   80: {
      //     component: PtEthWithdraw
      //   },
      //   81: {
      //     component: PtUsdcWithdraw
      //   }
      // },
      followerResults: [],
      currentComponent: PtCpsWithdraw,
      ewalletCurrentComponent: PtCpsWithdraw,
      cryptoSelectWithdrawType: null,
      cpsWithdrawType: null,
      isShowSticPay: false,
      percentageFee: 0,
      showFee: false,
      creditCard: {
        cardHolderName: '',
        cardNumber: '',
        cardExpiryDate: ''
      },
      account: {
        accountNumber: '',
        currency: '',
        sessionNumber: ''
      },
      info: {
        balance: 0,
        profitSplitPercent: 0,
        refundAmount: 0,
        profitSplit: 0,
        payoutAmount: 0,
        profitSplitFee: 0,
        refundAmountFee: 0
      },
      // mainWrapperWidth: 0,
      ACCOUNT_TYPE_REV,
      form: {
        paymentMethod: 'crypto',
        crypto: null,
        ewallet: null,
        withdrawalType: -1
      },
      formRules: {
        paymentMethod: [
          {
            required: true,
            message: this.$t('common.formValidation.dynamicReq', {
              dynamic: this.$t('propTrade.createAccount.subscriptionDetail.subTitle')
            }),
            trigger: 'change'
          }
        ]
      }
    };
  },
  computed: {
    ...mapState('common', ['poaAuditStatus']),
    ...mapState('cps', ['withdrawalPayment']),
    ...mapState('propTrading', {
      hasPropTradingSession: 'session',
      hasPropTradingAccount: 'account'
    }),
    lang() {
      return this.$store.state.common.lang;
    },
    hasCreditCard() {
      return !!this.creditCard.cardNumber;
    },
    hasRefund() {
      return this.info.refundAmount > 0;
    },
    calPayoutMethodList() {
      const payoutMethodList = [
        {
          name: this.$t('propTrade.createAccount.subscriptionDetail.crypto'),
          key: PAYOUT_METHOD.CRYPTO
        }
      ];

      if (this.displayEwalletList) {
        payoutMethodList.push({
          name: this.$t('propTrade.createAccount.subscriptionDetail.ewallet'),
          key: PAYOUT_METHOD.EWALLET
        });
      }

      return payoutMethodList;
    },
    cryptoList() {
      return this.cryptoData;
    },
    ewalletList() {
      return this.cpsData.filter(
        payout => payout.id == this.sticPayId || payout?.cps?.currency_code === this.account.currency
      );
    },
    displayEwalletList() {
      return !!this.ewalletList.length;
    },
    routerAccountNumber() {
      return this.$route.params.accountNumber;
    },
    numberRouterAccountId() {
      return Number(this.$route.params.accountId);
    }
  },
  watch: {
    ewalletList(value) {
      if (value?.length) {
        this.cpsWithdrawType = value[0].id;
        this.form.ewallet = value[0].nameKey;
        this.$store.dispatch('cps/actionSetWithdrawalPayment', value[0]?.cps);
      }
    },
    async 'form.withdrawalType'() {
      this.showFee = false;
      try {
        const paymentInfo = await apiPaymentGetInfo({
          withdrawType: this.form.withdrawalType,
          currency: this.account.currency,
          withdrawalAmount: 0,
          mt4Account: this.account.accountNumber
        });
        this.percentageFee = paymentInfo?.data?.percentage_fee;
        this.showFee = paymentInfo?.data?.show_hint;

        const payoutInfo = await this.fetchPayoutInfo();

        if (!payoutInfo) {
          this.$router.replace({ name: 'propTradingDashboard' });
          return;
        }

        this.info.balance = payoutInfo.balance;
        this.info.profitSplitPercent = payoutInfo.profitSplitPercent;
        this.info.refundAmount = payoutInfo.refundAmount;
        this.info.profitSplit = payoutInfo.profitSplit;
        this.info.payoutAmount = payoutInfo.payoutAmount;
        this.info.profitSplitFee = payoutInfo.profitSplitFee;
        this.info.refundAmountFee = payoutInfo.refundAmountFee;
        this.creditCard.cardHolderName = payoutInfo.creditCardInfo?.nameOnCard ?? '';
        this.creditCard.cardNumber =
          payoutInfo.creditCardInfo?.cardFirstSixDigits && payoutInfo.creditCardInfo?.cardLastFourDigits
            ? payoutInfo.creditCardInfo.cardFirstSixDigits + '** ****' + payoutInfo.creditCardInfo.cardLastFourDigits
            : '';
        this.creditCard.cardExpiryDate =
          (payoutInfo.creditCardInfo?.expMonth ?? '') + '-' + (payoutInfo.creditCardInfo?.expYear ?? '');
      } catch (error) {
        console.log(error);
      }
    },
    async 'form.paymentMethod'(value) {
      if (value === 'crypto') {
        this.changeCryptoPayoutDetail(this.cryptoList?.[0]);
      } else {
        this.changeCPSPayoutDetail(this.ewalletList[0]);
      }
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      const accountMetrics = await this.getAccountMetrics();

      // if has no pt accounts or sessions (dashboard will not show) => direct to home page
      if (this.poaAuditStatus !== statusConfig.completed || !accountMetrics?.payout) {
        let targetPage = !this.hasPropTradingSession && !this.hasPropTradingAccount ? 'home' : 'propTradingDashboard';
        this.$router.push({ name: targetPage });
        return;
      }

      this.account.currency = accountMetrics.currency;
      this.account.accountNumber = accountMetrics.accountNumber;
      this.account.sessionNumber = accountMetrics.sessionNumber;

      await this.fetchPropTradingWithdrawalMethod(this.routerAccountNumber, this.account.currency);
      await this.$nextTick();

      this.form.crypto = this.cryptoList?.[0]?.label;
      this.cryptoSelectWithdrawType = this.cryptoList?.[0]?.id;
      this.form.withdrawalType = this.cryptoList?.[0]?.id;

      if (this.form.paymentMethod === 'crypto') {
        this.changeCryptoPayoutDetail(this.cryptoList?.[0]);
      } else {
        this.changeCPSPayoutDetail(this.ewalletList[0]);
      }
    },
    firstLetterUpperCase(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    closeDialog() {
      this.$router.replace({ name: 'propTradingDashboard' });
    },
    async fetchPayoutInfo() {
      const result = await apiPayoutInfo(this.routerAccountNumber, this.form.withdrawalType);
      if (result.data.code !== 0) {
        return null;
      }

      if (result.data.data.respCodeEnum !== 'SUCCESS') {
        return null;
      }

      return result.data.data;
    },
    async getAccountMetrics() {
      const result = await apiGetAccountMetrics(this.routerAccountNumber);

      if (result.data.code === 0) {
        return result.data.data;
      }

      return null;
    },
    goBackToChooseAccount() {
      this.$router.replace({ name: 'propTradingDashboard' });
    },
    async submit() {
      let childRef = null;
      if (this.form.paymentMethod === PAYOUT_METHOD.CRYPTO) {
        childRef = this.$refs.cryptoComponent;
      } else {
        childRef = this.$refs.cpsComponent;
      }
      const target = childRef.$refs['TransferFormWrapper'];
      const targetForm = childRef.$refs['TransferFormWrapper'].$refs['TransferForm'];

      try {
        const childFormValidate = await targetForm.validate();
        if (!childFormValidate) {
          return;
        }
      } catch (error) {
        return;
      }

      let childFormData = await target.getFormData();
      const { paymentMethodCode, mandatory, attachVariables, accountNumber, bankAccountName, importantNotes } =
        childFormData;

      childFormData = {
        ...childFormData,
        thirdPaymentCode: paymentMethodCode ?? '',
        thirdMandatory: mandatory ?? '',
        thirdAttachVariable: attachVariables ?? '',
        paymentAccountNumber: accountNumber ?? '',
        paymentAccountName: bankAccountName ?? '',
        paymentNotes: importantNotes ?? ''
      };

      console.log('childFormData', childFormData);

      /*
        REFUND(0,"Refund"),
        PROFIT(1,"Profit Split");
        */
      const requestBody = [];

      if (this.info.refundAmount > 0) {
        let refundData = {
          currency: this.account.currency,
          payoutType: 0,
          paymentType: this.form.withdrawalType,
          amount: this.info.refundAmount - this.info.refundAmountFee,
          accountNumber: this.routerAccountNumber
        };
        if (!this.hasCreditCard) {
          refundData = { ...childFormData, ...refundData };
        } else {
          refundData.paymentType = 1; //credit card
        }
        requestBody.push(refundData);
      }

      requestBody.push({
        ...childFormData,
        currency: this.account.currency,
        payoutType: 1,
        paymentType: this.form.withdrawalType,
        amount: this.info.profitSplit - this.info.profitSplitFee,
        accountNumber: this.routerAccountNumber
      });

      const result = await apiPropTradingPayout(requestBody, this.token);

      if (isSuccess(result.data)) {
        this.showSuccessDialog = true;
        return;
      }
      if (result.data.code == 1506) {
        this.showErrorDialog = true;
      }
    },
    async changeCryptoPayoutDetail(data) {
      // this.form.crypto = data?.label;
      // this.cryptoSelectWithdrawType = data.id;
      // this.form.withdrawalType = this.cryptoSelectWithdrawType;
      // this.currentComponent = this.withdrawalTypesMap[this.cryptoSelectWithdrawType].component;
      this.cryptoSelectWithdrawType = data.id;
      this.currentComponent = PtCpsWithdraw;
      await this.changeCPSPayoutDetail(data);
      this.form.crypto = data?.label;
      this.form.withdrawalType = this.cryptoSelectWithdrawType;
    },
    async changeCPSPayoutDetail(data) {
      this.form.ewallet = data?.label;
      if (data.id === this.sticPayId) {
        this.isShowSticPay = true;
        this.form.withdrawalType = this.sticPayId;
      } else {
        this.isShowSticPay = false;
        this.cpsWithdrawType = data.id;
        this.form.withdrawalType = this.cpsWithdrawType;
        await this.$store.dispatch('cps/actionSetWithdrawalPayment', data.cps);
      }
      await this.$nextTick();
      // clear other withdraw channels
      if (this.$refs.cpsComponent)
        this.$refs.cpsComponent.$refs['TransferFormWrapper'].$refs['TransferForm'].clearValidate();
    },
    setPaymentDetailDefault(value) {
      if (value === PAYOUT_METHOD.CRYPTO) {
        this.form.withdrawalType = this.cryptoSelectWithdrawType;
      } else {
        this.form.withdrawalType = this.cpsWithdrawType;
      }
    },
    langTranslate(prefix, key) {
      const filteredKey = key ? key.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/\s]/g, '').toLowerCase() : '';
      const comparedKey = prefix + filteredKey;
      const translatedLang = this.$t(prefix + filteredKey);

      return comparedKey === translatedLang ? key : translatedLang;
    }
  }
};
</script>
<style lang="scss" scoped>
#prop_trading_payout {
  .card {
    background-color: $white;
    border-radius: 8px;
    margin-bottom: 100px;
  }
  .content-box {
    padding: 40px 100px;
    .title {
      font-size: 20px;
      font-weight: 700;
      color: $primary-dark-blue;
      margin-bottom: 25px;
    }

    .label_title {
      display: flex;
      justify-content: space-between;
    }
    .sub_label,
    .sub-title {
      display: flex;
      font-size: 16px;
      font-weight: 700;
      color: $primary-dark-blue;
      align-items: center;
    }
    .label_amount {
      color: $primary-dark-blue;
      font-size: 20px;
      font-weight: 600;
    }

    .refund {
      padding-bottom: 32px;
      border-bottom: 0.5px dashed $primary-dark-blue;
      color: $primary-dark-blue;
      &_detail {
        margin-top: 24px;
        .disabled_input {
          background-color: #f4f4f4;
          color: #949494;
          width: 100%;
          border: 1px solid #cdcfd3;
          position: relative;
          display: flex;
          align-items: center;
          padding: 0 15px;
          gap: 12px;

          .calendar_icon {
            width: 16px;
            height: 16px;
          }
        }
      }
      &.has-cc {
        padding-bottom: 10px;
      }
    }
    .profit_split {
      padding-top: 32px;
      border-bottom: 0.5px dashed $primary-dark-blue;
    }
    .payment-method {
      > .el-form-item {
        margin-bottom: 8px;
      }
      .payment-method-radio-box {
        display: grid;
        flex-basis: 50%;
        flex-wrap: nowrap;
        grid-template-columns: 1fr 1fr;
        .payment-method-item {
          margin-bottom: 20px;
          @include rtl-sass-prop(margin-right, margin-left, 20px);
          &:last-child {
            @include rtl-sass-prop(margin-right, margin-left, 0);
          }
          .el-radio-group {
            width: 100%;
          }
          /deep/ .el-radio-button {
            width: 100%;
            .el-radio-button__inner {
              width: 100%;
              border-radius: 59px;
            }
          }
        }
      }
    }
    .payment-method-group {
      .sub-title {
        padding-bottom: 24px;
      }
      .payment-radio {
        /deep/ .el-radio-group {
          display: flex;
          flex-wrap: wrap;
          gap: 20px 40px;
        }
        /deep/ .el-radio {
          margin-bottom: 8px;
        }
        /deep/ .el-radio__label {
          color: $primary-dark-blue;
          font-weight: 600;
          @include rtl-sass-prop(padding-left, padding-right, 10px);
        }
      }
    }

    .summary {
      background-color: #0051ff08;
      padding: 20px;
      border-radius: 12px;
      margin: 32px 0;
      &_title {
        font-size: 16px;
        color: #323232;
        font-weight: 700;
        margin-bottom: 0.875rem;
      }
      .value {
        font-size: 14px;
      }
      &_detail {
        li {
          font-size: 14px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: #323232;
          margin-bottom: 0.875rem;
          font-weight: 500;
          line-height: 21px;
          .join_line {
            display: inline-block;
            flex: 1;
            margin: 0 10px;
            border: 0.5px dashed #b2b2b2;
            opacity: 0.5;
            height: 0;
          }
          // &.dash {
          //   border-bottom: 1px dashed $stone-gray;
          // }
          &.total {
            font-weight: 700;
            align-items: center;
            .label {
              font-size: 16px;
              display: inline-block;
              align-items: center;
              span {
                max-width: unset;
                display: inline-block;
              }
            }
            .value {
              font-size: 24px;
              min-width: fit-content;
            }
          }
        }
      }
    }
    .btn-box {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 20px;
      flex-wrap: wrap;
      > div {
        flex-basis: 40%;
        max-width: 230px;
      }
      .action-button {
        padding: 10px 20px;
        border-radius: 35px;
        font-weight: 600;
        width: 100%;
        height: 40px;
        line-height: 1;
      }
      .back-button .action-button {
        border: 1px solid $primary-blue;
        color: $primary-blue;
        background-color: $white;
      }
    }
  }
  /deep/ .ptDialog {
    .desc_title {
      margin-bottom: 20px;
    }
  }
  hr {
    border-style: dashed;
    border-color: $stone-gray;
  }

  /deep/ .el-dialog__wrapper {
    .el-dialog {
      width: 460px !important;
    }
  }
}

@media (max-width: 768px) {
  #prop_trading_payout {
    .content-box {
      padding: 15px;
      .title {
        font-size: 16px;
      }
      .sub_label,
      .sub-title {
        font-size: 14px;
      }
      .label_amount {
        font-size: 16px;
      }
      .refund {
        padding-bottom: 20px;
      }
      .profit_split {
        padding-top: 20px;
        border-bottom: none;
      }
      .payment-method {
        .payment-method-radio-box {
          flex-basis: auto;
          flex-wrap: wrap;
        }
      }
      .summary {
        padding: 12px;
        border-radius: 10px;
        margin: 10px 0 15px 0;
        &_title {
          font-size: 14px;
        }
        .value {
          font-size: 12px;
        }
        &_detail {
          li {
            font-size: 12px;
            &.total {
              .label {
                font-size: 14px;
                display: flex;
                span {
                  max-width: 120px;
                }
              }
              .value {
                font-size: 16px;
              }
            }
          }
        }
      }
      .btn-box {
        > div {
          max-width: none;
        }
      }
    }
    /deep/ .el-form-item {
      margin-bottom: 20px;
    }
    /deep/ .el-form-item__label {
      margin-bottom: 10px;
    }

    /deep/ .el-dialog__wrapper {
      .el-dialog {
        width: 340px !important;
      }
      .el-dialog__body {
        padding: 0 15px 40px;
      }
    }
    .blue_button,
    .white_button {
      margin-bottom: 0;
    }
  }
}
</style>
